// The rules of the build flow that decide what a program can be built on, with
// no React in them so they can be tested on their own (trainingRules.test.ts).
//
// Every program is for roles, and builds only the capabilities every chosen
// role shares, from its type's source: everyone in the program then holds each
// capability in it, so everyone can be graded on every Roleplay. A role's own
// capabilities are shared between roles of one team only, so roles from
// different teams never have one of those in common; the organization's shared
// ones (Development, Standards) cross teams. Every capability and every
// learning outcome gets a lesson of its own. A program with no capability is
// built on its learning outcomes, at least three of them.

import type { SetupCapability, SetupRole } from '../capabilitySetup';
import { SOURCE_LABEL, type CapabilitySource } from '../capabilitySource';
import { isSameCapability } from '../sharedCapabilities';
import { appliedTo, type CapabilitySet } from '../sharedSets';
import type { ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';

export type FormatKey = 'course' | 'shortCourse' | 'brief';

/** A program with no capability needs at least this many learning outcomes. */
export const MIN_OUTCOMES = 3;

/**
 * The capabilities every one of these roles holds (the same capability: see
 * ../sharedCapabilities.ts), as the first role has them. One role: all of its
 * own. No roles, or roles of more than one team: none, since a capability is
 * shared within a team only.
 */
export const capabilitiesInCommon = (roles: SetupRole[]): SetupCapability[] => {
  if (roles.length === 0) return [];
  const [first, ...rest] = roles;
  if (rest.some((r) => r.teamId !== first.teamId)) return [];
  return first.capabilities.filter((c) => rest.every((r) => r.capabilities.some((x) => isSameCapability(x, c))));
};

/**
 * A program's type decides which of its roles' capabilities it can build.
 * Role Excellence: the roles' own, shared by every chosen role (one team).
 * Development and Standards: the shared ones of that set applied to every
 * chosen role, whatever their teams.
 */
export const sourceForType = (category: ProgramTypeKey | null): CapabilitySource =>
  category === 'development' || category === 'standards' ? category : 'role';

/** The capabilities a program of this type can build for these roles: those every one of them holds, from its type's source. */
export const capabilitiesForType = (
  category: ProgramTypeKey | null,
  roles: SetupRole[],
  sets: CapabilitySet[]
): SetupCapability[] => {
  const source = sourceForType(category);
  if (source === 'role') return capabilitiesInCommon(roles);
  if (roles.length === 0) return [];
  const [first, ...rest] = roles.map((r) => appliedTo(r, sets).filter((c) => c.source === source));
  return first.filter((c) => rest.every((held) => held.some((x) => x.id === c.id)));
};

/** What the Details step says under the roles a Training is for, as they are chosen: what its type can build on them. */
export const rolesNoteForType = (
  category: ProgramTypeKey | null,
  chosen: SetupRole[],
  sets: CapabilitySet[]
): string => {
  const source = sourceForType(category);
  const label = source === 'role' ? '' : `${SOURCE_LABEL[source]} `;
  if (chosen.length === 0) {
    return `Choose the roles this program is for. It can build the ${label}capabilities every chosen role shares.`;
  }
  const shared = capabilitiesForType(category, chosen, sets).length;
  if (shared > 0) {
    const kind = label.toLowerCase();
    return chosen.length === 1
      ? `${chosen[0].name} has ${count(shared, kind)}.`
      : `These roles share ${count(shared, kind)}.`;
  }
  if (source === 'role' && new Set(chosen.map((r) => r.teamId)).size > 1) {
    return `These roles are in different teams, and a role's own capabilities are only shared within a team, so this program will be built on learning outcomes.`;
  }
  return chosen.length === 1
    ? `${chosen[0].name} has no ${label}capabilities, so this program will be built on learning outcomes.`
    : `These roles share no ${label}capabilities, so this program will be built on learning outcomes.`;
};

const count = (n: number, label = '') => `${n} ${label}${n === 1 ? 'capability' : 'capabilities'}`;

/** What the Details step says under the roles an Assessment is for: each person answers their own role's. */
export const assessmentRolesNote = (chosen: SetupRole[]): string =>
  chosen.length === 0
    ? 'Choose the roles to assess. Each person answers only the capabilities of their own role.'
    : 'Each person answers only the capabilities of their own role.';

/** How many goals a format has room for: each needs a lesson of its own. A Course grows to fit. */
const CAPABILITY_ROOM: Record<FormatKey, number | null> = { course: null, shortCourse: 4, brief: 1 };

/**
 * Every capability and every learning outcome gets a lesson of its own, so a
 * format holds as many goals as it has lessons: a Brief one, a Short Course
 * four; a Course grows to fit. Why a format can't be chosen for this many
 * goals, or null when it can.
 */
export const lessonsLock = (format: FormatKey, goals: number): string | null => {
  const room = CAPABILITY_ROOM[format];
  if (room === null || goals <= room) return null;
  return format === 'brief'
    ? `A Brief is one lesson. Each capability and learning outcome gets its own, and you chose ${goals}, so choose a Short Course or a Course.`
    : `A Short Course is up to four lessons, one for each capability and learning outcome; you chose ${goals}.`;
};
